/**
 * `declareVariants` builds a `variants` closure from a state object whose keys
 * are the component's variant axes (string-union or boolean values). Each
 * `variants(cases)` call returns a className string assembled from the cases
 * that apply to the current state.
 *
 * Why: keeps a component's per-element styling co-located with its JSX while
 * still giving you exhaustive checking on enum axes (TypeScript errors if you
 * forget a variant value), plus a `when` escape-hatch for compound rules.
 *
 * Example (default = exhaustive):
 *
 *   const variants = declareVariants({
 *     variant,    // 'default' | 'borderless'
 *     size,       // 'md' | 'sm'
 *     isOpen,     // boolean
 *   });
 *
 *   <button className={cn(
 *     'base classes here',
 *     variants({
 *       variant: { default: 'border ...', borderless: 'border-transparent ...' },
 *       size:    { md: 'h-9 ...', sm: 'h-7 ...' },
 *       isOpen:  'bg-grey-70',          // shorthand: only applied when isOpen === true
 *       inline:  ['relative …', 'absolute …'], // tuple: [true case, false case]
 *       when: [
 *         [{ variant: 'default', isOpen: true }, 'border-pressed!'],
 *         // OR: first element is an array of partial state objects — matches if any branch matches (each branch is AND).
 *         [[{ variant: 'a', disabled: true }, { variant: 'b', disabled: true }], 'opacity-50'],
 *       ],
 *     }),
 *   )} />
 *
 * Pass `{ exhaustive: false }` to allow partial axes (missing values are
 * treated as "no class") if you don't want the strictness.
 */

import { cn } from './cn';

type ClassValue = string | false | null | undefined;

/** Boolean axis: plain value = true-only; `[a, b]` = true → a, false → b. */
type BooleanAxisCase = ClassValue | readonly [ClassValue, ClassValue];

/**
 * Cases for one axis. Booleans expand to `'true' | 'false'` keys (which is how
 * object literals like `{ true: '...' }` are typed in TypeScript anyway).
 *
 * For boolean axes, a bare `ClassValue` is also accepted as a shorthand for
 * "apply these classes only when the state value is `true`":
 *
 *   isOpen: 'bg-grey-70'
 *   // ↳ equivalent to: { true: 'bg-grey-70', false: undefined }
 *
 * A two-element tuple sets both branches — index 0 when `true`, index 1 when
 * `false` (use `undefined` in either slot to skip):
 *
 *   inline: ['relative shadow-md', 'absolute z-50 …']
 *
 * The `[V] extends [...]` tuple wrap is intentional: it prevents the conditional
 * type from distributing over union members. Without it, `AxisCases<'md' | 'sm'>`
 * becomes `{ md: ClassValue } | { sm: ClassValue }` (either-or) instead of the
 * intended `{ md: ClassValue; sm: ClassValue }` (both required).
 */
type ExhaustiveAxisCases<V> = [V] extends [boolean]
	? BooleanAxisCase | { [K in `${V}`]: ClassValue }
	: [V] extends [string]
		? { [K in `${V}`]: ClassValue }
		: never;

/** Permissive variant — every value is optional. */
type PartialAxisCases<V> = [V] extends [boolean]
	? BooleanAxisCase | Partial<{ [K in `${V}`]: ClassValue }>
	: [V] extends [string]
		? Partial<{ [K in `${V}`]: ClassValue }>
		: never;

type AxisCases<V, Exhaustive extends boolean> = [Exhaustive] extends [true]
	? ExhaustiveAxisCases<V>
	: PartialAxisCases<V>;

/** `when` predicate: one partial (AND) or an array of partials (OR of ANDs). */
export type WhenPredicate<S> = Partial<S> | ReadonlyArray<Partial<S>>;

export type MatchCases<S, Exhaustive extends boolean = true> = Partial<{
	[K in keyof S]: AxisCases<S[K], Exhaustive>;
}> & {
	when?: ReadonlyArray<readonly [WhenPredicate<S>, ClassValue]>;
};

function matchesPartial<S extends Record<string, string | boolean>>(
	state: S,
	partial: Partial<S>,
): boolean {
	return Object.entries(partial).every(
		([k, v]) => state[k as keyof S] === v,
	);
}

function matchesWhenPredicate<S extends Record<string, string | boolean>>(
	state: S,
	pred: WhenPredicate<S>,
): boolean {
	if (Array.isArray(pred)) {
		return pred.some((branch) => matchesPartial(state, branch));
	}
	return matchesPartial(state, pred as Partial<S>);
}

/**
 * Argument shape for the `variants` closure.
 *
 * - Axes you want to react to are listed by key. With `exhaustive: true` (the
 *   default), each listed axis must be exhaustive — every possible value of
 *   that axis needs an entry. Use `undefined` / `false` to opt-out of
 *   contributing classes for a given case (explicit "no-op"). With
 *   `exhaustive: false`, missing values are silently treated as "no class".
 * - `when` is a selective compound-rule list: `[predicate, classes]` pairs.
 *   Predicate is either a partial state object (every listed key must match — AND)
 *   or a non-empty array of such partials (any branch may match — OR of ANDs).
 */

export interface MakeMatchOptions<Exhaustive extends boolean = true> {
	/**
	 * When true (default), every axis you reference in `variants(cases)` must list
	 * a class for every possible value of that axis (use `undefined`/`false` to
	 * opt out of contributing a class for a particular value).
	 *
	 * When false, axes can be partially specified — missing values are treated
	 * as "no class".
	 */
	exhaustive?: Exhaustive;
}

/**
 * Returns a `variants` closure bound to `state`.
 *
 * Overloaded so the `cases` type follows the literal `exhaustive` value the
 * caller passes, without relying on a `const` type parameter (which the
 * project's babel-loader doesn't yet parse).
 */
export function declareVariants<S extends Record<string, string | boolean>>(
	state: S,
	options: { exhaustive: false },
): (cases: MatchCases<S, false>) => string;
export function declareVariants<S extends Record<string, string | boolean>>(
	state: S,
	options?: { exhaustive?: true },
): (cases: MatchCases<S, true>) => string;
export function declareVariants<S extends Record<string, string | boolean>>(
	state: S,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
	options?: MakeMatchOptions<boolean>,
) {
	return function match(cases: MatchCases<S, boolean>): string {
		const out: ClassValue[] = [];

		for (const key of Object.keys(cases) as Array<keyof MatchCases<S, boolean>>) {
			if (key === 'when') continue;
			const axis = cases[key];
			if (!axis) continue;
			const value = state[key as keyof S];
			if (Array.isArray(axis)) {
				const tuple = axis as readonly [ClassValue, ClassValue];
				out.push(value === true ? tuple[0] : tuple[1]);
			} else if (typeof axis === 'object' && axis !== null) {
				out.push((axis as Record<string, ClassValue>)[String(value)]);
			} else if (value === true) {
				// Boolean shorthand: `axis` is a ClassValue that applies when state is true.
				out.push(axis as ClassValue);
			}
		}

		for (const [pred, cls] of cases.when ?? []) {
			if (matchesWhenPredicate(state, pred)) out.push(cls);
		}

		return cn(...out);
	};
}
